import { ConnectedPosition, Overlay, OverlayRef } from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import {
  ComponentRef,
  Directive,
  ElementRef,
  inject,
  input,
  NgZone,
  OnDestroy,
  OnInit,
  ViewContainerRef,
} from '@angular/core';
import { HxPopover, PopoverPosition } from '../controls/popover';

/**
 * Ellipsis Tooltip Directive
 *
 * Automatically shows a popover tooltip when the host element's text is truncated (i.e., has an ellipsis). When the
 * text fits without truncation, no tooltip is shown.
 *
 * Reuses the existing `HxPopover` overlay component internally.
 *
 * @example
 *   ```html
 *   <span hxEllipsisTooltip>Long text that may overflow...</span>
 *   ```;
 *
 * @example
 *   with custom tooltip text
 *   ```html
 *   <span [hxEllipsisTooltip]="'Full descriptive text'">Short...</span>
 *   ```
 *
 * @example
 *   with custom position
 *   ```html
 *   <span hxEllipsisTooltip ellipsisPosition="bottom">Overflow text</span>
 *   ```
 */
@Directive({
  selector: '[hxEllipsisTooltip]',
  standalone: true,
  host: {
    style: 'overflow: hidden; text-overflow: ellipsis; white-space: nowrap; min-width: 0;',
  },
})
export class EllipsisTooltipDirective implements OnInit, OnDestroy {
  private readonly overlay = inject(Overlay);
  private readonly vcr = inject(ViewContainerRef);
  private readonly el = inject(ElementRef<HTMLElement>);
  private readonly zone = inject(NgZone);

  private overlayRef?: OverlayRef;
  private popoverRef?: ComponentRef<HxPopover>;
  private showTimeout?: number;
  private hideTimeout?: number;

  /** Custom tooltip text. Falls back to the host element's `textContent`. */
  readonly hxEllipsisTooltip = input<string>('');

  /** Popover position. Defaults to `'top'`. */
  readonly ellipsisPosition = input<PopoverPosition>('top');

  /** Delay in ms before showing / hiding. */
  readonly ellipsisDelay = input<number>(100);

  /** When true, the tooltip is completely suppressed regardless of truncation. */
  readonly ellipsisDisabled = input<boolean>(false);

  private readonly onMouseEnter = () => {
    if (this.ellipsisDisabled()) return;
    this.showWithDelay();
  };
  private readonly onMouseLeave = () => this.hideWithDelay();
  private readonly onOverlayEnter = () => this.clearHideTimeout();
  private readonly onOverlayLeave = () => this.hideWithDelay();

  ngOnInit(): void {
    this.zone.runOutsideAngular(() => {
      const el = this.el.nativeElement;
      el.addEventListener('mouseenter', this.onMouseEnter);
      el.addEventListener('mouseleave', this.onMouseLeave);
    });
  }

  // ── Show / hide logic ────────────────────────────────────────────────

  private showWithDelay(): void {
    this.clearHideTimeout();
    this.showTimeout = window.setTimeout(() => this.show(), this.ellipsisDelay());
  }

  private hideWithDelay(): void {
    clearTimeout(this.showTimeout);
    this.hideTimeout = window.setTimeout(() => this.hide(), this.ellipsisDelay());
  }

  private clearHideTimeout(): void {
    clearTimeout(this.hideTimeout);
  }

  private show(): void {
    if (!this.isTextTruncated()) {
      return;
    }

    const text = this.resolveTooltipText();
    if (!text) {
      return;
    }

    this.zone.run(() => {
      if (!this.overlayRef) {
        this.createOverlay();
      }

      if (!this.overlayRef!.hasAttached()) {
        const portal = new ComponentPortal(HxPopover, this.vcr);
        this.popoverRef = this.overlayRef!.attach(portal);
        this.popoverRef.instance.content.set(text);
        this.popoverRef.instance.variant.set('pointer');
        this.popoverRef.instance.position.set(this.ellipsisPosition());
        this.applyTooltipWrappingStyles();
        window.setTimeout(() => this.applyTooltipWrappingStyles(), 0);

        // Keep popover open while the user hovers over it
        this.overlayRef!.overlayElement.addEventListener('mouseenter', this.onOverlayEnter);
        this.overlayRef!.overlayElement.addEventListener('mouseleave', this.onOverlayLeave);
      }
    });
  }

  private hide(): void {
    if (this.overlayRef) {
      this.overlayRef.overlayElement.removeEventListener('mouseenter', this.onOverlayEnter);
      this.overlayRef.overlayElement.removeEventListener('mouseleave', this.onOverlayLeave);
    }
    this.overlayRef?.detach();
    this.popoverRef = undefined;
  }

  // ── Overlay creation ─────────────────────────────────────────────────

  private createOverlay(): void {
    const positionStrategy = this.overlay
      .position()
      .flexibleConnectedTo(this.el)
      .withFlexibleDimensions(false)
      .withPush(true)
      .withPositions(this.getPositions());

    positionStrategy.positionChanges.subscribe((change) => {
      if (this.popoverRef) {
        const actual = this.resolveActualPosition(change);
        this.popoverRef.instance.position.set(actual);
      }
    });

    this.overlayRef = this.overlay.create({
      positionStrategy,
      scrollStrategy: this.overlay.scrollStrategies.close(),
    });
  }

  // ── Position helpers ─────────────────────────────────────────────────

  private getPositions(): ConnectedPosition[] {
    const offset = 8;
    const pos = this.ellipsisPosition();

    const map: Record<PopoverPosition, ConnectedPosition> = {
      top: { originX: 'center', originY: 'top', overlayX: 'center', overlayY: 'bottom', offsetY: -offset },
      bottom: { originX: 'center', originY: 'bottom', overlayX: 'center', overlayY: 'top', offsetY: offset },
      left: { originX: 'start', originY: 'center', overlayX: 'end', overlayY: 'center', offsetX: -offset },
      right: { originX: 'end', originY: 'center', overlayX: 'start', overlayY: 'center', offsetX: offset },
      'top-start': { originX: 'start', originY: 'top', overlayX: 'start', overlayY: 'bottom', offsetY: -offset },
      'top-end': { originX: 'end', originY: 'top', overlayX: 'end', overlayY: 'bottom', offsetY: -offset },
      'bottom-start': { originX: 'start', originY: 'bottom', overlayX: 'start', overlayY: 'top', offsetY: offset },
      'bottom-end': { originX: 'end', originY: 'bottom', overlayX: 'end', overlayY: 'top', offsetY: offset },
      center: { originX: 'start', originY: 'top', overlayX: 'start', overlayY: 'top' },
    };

    const fallbacks: Record<PopoverPosition, PopoverPosition> = {
      top: 'bottom',
      bottom: 'top',
      left: 'right',
      right: 'left',
      'top-start': 'bottom-start',
      'top-end': 'bottom-end',
      'bottom-start': 'top-start',
      'bottom-end': 'top-end',
      center: 'center',
    };

    return [map[pos], map[fallbacks[pos]]];
  }

  private resolveActualPosition(change: any): PopoverPosition {
    const { originX, originY, overlayX, overlayY } = change.connectionPair;

    if (originY === 'top' && overlayY === 'bottom') {
      if (originX === 'start' && overlayX === 'start') return 'top-start';
      if (originX === 'end' && overlayX === 'end') return 'top-end';
      return 'top';
    }
    if (originY === 'bottom' && overlayY === 'top') {
      if (originX === 'start' && overlayX === 'start') return 'bottom-start';
      if (originX === 'end' && overlayX === 'end') return 'bottom-end';
      return 'bottom';
    }
    if (originX === 'start' && overlayX === 'end') return 'left';
    if (originX === 'end' && overlayX === 'start') return 'right';
    return 'bottom';
  }

  // ── Ellipsis detection ───────────────────────────────────────────────

  private isTextTruncated(): boolean {
    const el = this.el.nativeElement;
    return el.offsetWidth < el.scrollWidth || el.offsetHeight < el.scrollHeight;
  }

  private resolveTooltipText(): string {
    const custom = this.hxEllipsisTooltip();
    if (custom) {
      return custom;
    }
    return (this.el.nativeElement.textContent ?? '').trim();
  }

  private applyTooltipWrappingStyles(): void {
    const popoverElement = this.overlayRef?.overlayElement.querySelector('.hx-popover') as HTMLElement | null;
    if (!popoverElement) {
      return;
    }

    popoverElement.style.whiteSpace = 'normal';
    popoverElement.style.overflowWrap = 'anywhere';
    popoverElement.style.wordBreak = 'break-word';

    // The text content is rendered as a child span by HxPopover.
    const contentElement = popoverElement.querySelector('span:not(.arrow)') as HTMLElement | null;
    if (contentElement) {
      contentElement.style.fontSize = '16px';
      contentElement.style.display = 'block';
      contentElement.style.whiteSpace = 'normal';
      contentElement.style.overflowWrap = 'anywhere';
      contentElement.style.wordBreak = 'break-word';
    }
  }

  // ── Cleanup ──────────────────────────────────────────────────────────

  ngOnDestroy(): void {
    const el = this.el.nativeElement;
    el.removeEventListener('mouseenter', this.onMouseEnter);
    el.removeEventListener('mouseleave', this.onMouseLeave);
    this.hide();
    this.overlayRef?.dispose();
  }
}
